<template>
    <div
        v-if="todisplay"
        v-show="isvisible"
        :style="hostStyle"
        :class="hostClass"
    >
        <q-banner
            v-if="resolveError"
            class="bg-negative text-white q-ma-md"
            rounded
        >
            <template #avatar>
                <q-icon name="error" color="white" />
            </template>
            <div class="text-weight-medium">{{ resolveError }}</div>
            <div v-if="knownPageIds.length" class="q-mt-sm text-caption">
                Registered pages: {{ knownPageIds.join(', ') }}
            </div>
            <div v-else class="q-mt-sm text-caption">
                No pages registered under Application/pages/.
            </div>
        </q-banner>

        <component
            v-else-if="resolvedPage"
            :is="resolvedPage"
            :key="uniqueKey"
            :params="pageParams"
            :route="pageRoute"
            class="c-NativeVueHost-page"
            style="flex: 1 1 auto; min-height: 0; min-width: 0;"
        />
    </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';

export default {
    name: 'native-vue-host',
    mixins: [BaseComponent],
    data() {
        return {
            uniqueKey: utils.generateUUID(),
            componentEval: null,
            paramsEval: null,
            routeEval: null,
            resolvedPage: null,
            resolveError: null,
            cachedComponentId: null,
            /** Stable params object — only replaced when evaluated JSON content changes. */
            pageParamsStable: {},
            pageParamsStableKey: '',
        };
    },
    computed: {
        componentId() {
            const raw = this.controlData?.Component;
            if (raw === undefined || raw === null || raw === '')
                return '';
            if (!this.componentEval)
                this.componentEval = utils.compile(this, raw);
            const value = this.componentEval ? utils.evaluate(this.componentEval, this) : raw;
            return value === undefined || value === null ? '' : String(value);
        },
        /**
         * Evaluated ControlData.Params content key (for change detection).
         * Nested objects/arrays are JSON-normalized so reference churn is ignored.
         */
        pageParamsKey() {
            const raw = this.controlData?.Params;
            if (!raw)
                return '';

            if (!this.paramsEval)
                this.paramsEval = utils.compileObject(this, raw);

            const evaluated = this.paramsEval
                ? utils.evaluateObject(this.paramsEval, this)
                : raw;

            if (!evaluated || typeof evaluated !== 'object' || Array.isArray(evaluated))
                return '';

            try {
                return JSON.stringify(evaluated);
            }
            catch {
                return '';
            }
        },
        /**
         * Params passed to the mounted page. Same object identity until pageParamsKey changes
         * (avoids deep watchers on the page remounting grids every host re-render).
         */
        pageParams() {
            return this.pageParamsStable;
        },
        pageRoute() {
            const raw = this.controlData?.Route;
            if (raw === undefined || raw === null || raw === '')
                return '';
            if (!this.routeEval)
                this.routeEval = utils.compile(this, raw);
            const value = this.routeEval ? utils.evaluate(this.routeEval, this) : raw;
            return value === undefined || value === null ? '' : String(value);
        },
        knownPageIds() {
            return Object.keys(utils.nativePages || {}).sort();
        },
        hostClass() {
            const classes = {
                'c-NativeVueHost': true,
                [`c-name-${this.name || 'unnamed'}`]: true,
            };

            if (this.styleHints?.CustomClasses) {
                const customClasses = utils.resolveCustomClasses(this.styleHints, this);
                if (customClasses) {
                    for (const name of customClasses.split(' ')) {
                        if (name)
                            classes[name] = true;
                    }
                }
            }

            return classes;
        },
        hostStyle() {
            return {
                ...this.sizeStyle,
                ...utils.resolveStyleHints(this.styleHints, this),
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
            };
        },
    },
    watch: {
        componentId: {
            immediate: true,
            handler(id) {
                this.resolvePage(id);
            },
        },
        pageParamsKey: {
            immediate: true,
            handler(key) {
                if (key === this.pageParamsStableKey)
                    return;
                this.pageParamsStableKey = key;
                if (!key) {
                    this.pageParamsStable = {};
                    return;
                }
                try {
                    this.pageParamsStable = JSON.parse(key);
                }
                catch {
                    this.pageParamsStable = {};
                }
            },
        },
    },
    methods: {
        async preRenderComplete() {
            this.resolvePage(this.componentId);
            this.finishRenderHandler(this);
        },
        Refresh() {
            this.uniqueKey = utils.generateUUID();
        },
        resolvePage(id) {
            if (!id) {
                this.resolvedPage = null;
                this.resolveError = 'NativeVueHost: ControlData.Component is required.';
                this.cachedComponentId = null;
                return;
            }

            if (id === this.cachedComponentId && this.resolvedPage)
                return;

            const loader = utils.nativePages?.[id];
            if (!loader) {
                this.resolvedPage = null;
                this.resolveError = `NativeVueHost: unknown Component "${id}".`;
                this.cachedComponentId = null;
                return;
            }

            this.resolveError = null;
            this.cachedComponentId = id;
            this.uniqueKey = utils.generateUUID();

            if (typeof loader === 'function') {
                this.resolvedPage = markRaw(defineAsyncComponent(loader));
            }
            else {
                this.resolvedPage = markRaw(loader);
            }
        },
    },
};
</script>
